import { lazy } from "react";

/**
 * Wrapper de React.lazy() con reintentos automáticos para manejar
 * errores de carga de chunks en producción (ej: después de un deploy).
 *
 * Cuando Vite genera un nuevo build, los chunks cambian de nombre (hashes).
 * Si el Service Worker o el navegador sirven HTML cacheado que referencia
 * chunks viejos, el import() falla con "TypeError: Importing a module script failed".
 *
 * Esta utilidad:
 * 1. Intenta cargar el módulo normalmente
 * 2. Si falla, limpia los Service Worker caches relevantes
 * 3. Reintenta la carga una vez más
 * 4. Si sigue fallando, fuerza recarga de la página (una sola vez)
 *
 * @param {() => Promise} importFn - Función de import dinámico, ej: () => import("./MiComponente")
 * @param {string} [chunkName] - Nombre descriptivo para logs (opcional)
 * @returns {React.LazyExoticComponent} Componente lazy con retry
 */
export default function lazyWithRetry(importFn, chunkName = "unknown") {
  return lazy(() =>
    importFn().catch((error) => {
      console.warn(
        `[lazyWithRetry] Error cargando chunk "${chunkName}":`,
        error.message,
      );

      // Verificar si ya intentamos recargar para evitar loops infinitos
      const reloadKey = `chunk_reload_${chunkName}`;
      const hasReloaded = sessionStorage.getItem(reloadKey);

      if (hasReloaded) {
        // Ya recargamos una vez, limpiar flag y lanzar el error original
        sessionStorage.removeItem(reloadKey);
        console.error(
          `[lazyWithRetry] Chunk "${chunkName}" falló después de recargar. Error original:`,
          error,
        );
        throw error;
      }

      // Intentar limpiar caches del Service Worker y reintentar
      return clearCachesAndRetry(importFn, chunkName, reloadKey, error);
    }),
  );
}

/**
 * Limpia caches, reintenta el import. Si falla de nuevo, recarga la página.
 */
async function clearCachesAndRetry(
  importFn,
  chunkName,
  reloadKey,
  originalError,
) {
  try {
    // Limpiar caches de assets del Service Worker
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      const assetCaches = cacheNames.filter(
        (name) => name.includes("assets") || name.includes("precache"),
      );
      await Promise.all(assetCaches.map((name) => caches.delete(name)));
      console.log(
        `[lazyWithRetry] Caches de assets limpiados: ${assetCaches.join(", ")}`,
      );
    }

    // Reintentar el import
    const module = await importFn();
    console.log(
      `[lazyWithRetry] Chunk "${chunkName}" cargado exitosamente después de limpiar cache`,
    );
    return module;
  } catch (retryError) {
    console.warn(
      `[lazyWithRetry] Reintento fallido para "${chunkName}". Recargando página...`,
    );

    // Marcar que vamos a recargar para evitar loop infinito
    sessionStorage.setItem(reloadKey, "true");

    // Forzar recarga desde el servidor (no desde cache)
    window.location.reload();

    // Retornar una promesa que nunca resuelve (la página se va a recargar)
    return new Promise(() => {});
  }
}
